import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Alert, Platform, FlatList } from 'react-native';
import AppTextInput from './AppTextInput';
import { Business } from '../types/business';
import { appointmentRequestService } from '../services/appointmentRequestService';
import { AuthManager } from '../utils/authManager';
import DatePicker from './DatePicker';
import TimePicker from './TimePicker';
import { getServiceSuggestions, searchServices } from '../utils/serviceSuggestions';
import { customServiceRequestService } from '../services/customServiceRequestService';

interface TransportationQuoteRequestFormProps {
  business: Business;
  onCancel: () => void;
  onSubmit: () => void;
}

const TransportationQuoteRequestForm: React.FC<TransportationQuoteRequestFormProps> = ({
  business,
  onCancel,
  onSubmit,
}) => {
  const [service, setService] = useState('');
  const [pickupAddress, setPickupAddress] = useState('');
  const [dropoffAddress, setDropoffAddress] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [userInfo, setUserInfo] = useState({
    name: '',
    phone: '',
    email: '',
    userId: '',
  });

  const [serviceSuggestions, setServiceSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([]);

  useEffect(() => {
    loadUserInfo();
    const suggestions = getServiceSuggestions('transportation');
    setServiceSuggestions(suggestions);
    setFilteredSuggestions(suggestions);
  }, []);

  const handleServiceChange = (text: string) => {
    setService(text);
    if (text.length > 0) {
      const matches = searchServices(text, business.category || 'transportation');
      setFilteredSuggestions(matches);
      setShowSuggestions(matches.length > 0);
    } else {
      setFilteredSuggestions(serviceSuggestions);
      setShowSuggestions(serviceSuggestions.length > 0);
    }
  };

  const handleSuggestionSelect = (suggestion: string) => {
    setService(suggestion);
    setShowSuggestions(false);
  };

  const handleBlur = () => {
    setTimeout(() => setShowSuggestions(false), 200);
  };

  const loadUserInfo = async () => {
    try {
      let user: any = null;
      if (Platform.OS === 'web') {
        const { UnifiedAuthService } = await import('../services/unifiedAuthService');
        user = await UnifiedAuthService.getCurrentUser();
      } else {
        user = await AuthManager.getSavedUser();
      }

      if (user) {
        let phone = '';
        if (user.phone) {
          phone = user.phone;
        } else if (typeof user.address === 'object' && user.address?.phone) {
          phone = user.address.phone;
        }

        let profileAddress = '';
        if (typeof user.address === 'string') {
          profileAddress = user.address;
        } else if (user.address && typeof user.address === 'object') {
          const a = user.address as {
            street?: string;
            city?: string;
            state?: string;
            zipcode?: string;
          };
          profileAddress = [a.street, a.city, a.state, a.zipcode].filter(Boolean).join(', ');
        }

        setUserInfo({
          name: user.name || '',
          phone: phone,
          email: user.email || user.username || '',
          userId: user.id || user.uid || `user_${Date.now()}`,
        });

        if (profileAddress.trim()) {
          setPickupAddress((prev) => (prev.trim() ? prev : profileAddress.trim()));
        }
      }
    } catch (error) {
      console.error('Error loading user info:', error);
    }
  };

  const getTodayDate = () => new Date().toISOString().split('T')[0];
  const minDate = getTodayDate();

  const extractZip = (addr?: string) => {
    const m = addr?.match(/\b\d{5}\b/);
    return m ? m[0] : '';
  };

  const handleSubmit = async () => {
    if (!service.trim()) {
      Alert.alert('Error', 'Please enter the type of ride or service (e.g. airport shuttle, taxi).');
      return;
    }
    if (!pickupAddress.trim()) {
      Alert.alert('Error', 'Please enter a pickup address (from).');
      return;
    }
    if (!dropoffAddress.trim()) {
      Alert.alert('Error', 'Please enter a drop-off address (to).');
      return;
    }
    if (!date.trim()) {
      Alert.alert('Error', 'Please select a preferred date.');
      return;
    }
    if (!time.trim()) {
      Alert.alert('Error', 'Please select a preferred time.');
      return;
    }
    if (!userInfo.name || !userInfo.phone) {
      Alert.alert('Error', 'Please complete your name and phone.');
      return;
    }

    setLoading(true);
    const zipFromBusiness = extractZip(business.address);
    const zipFromPickup = extractZip(pickupAddress);
    const zipcode = zipFromBusiness || zipFromPickup || '';

    const descriptionParts = [
      `Transportation quote request: ${service.trim()}`,
      `Pickup: ${pickupAddress.trim()}`,
      `Drop-off: ${dropoffAddress.trim()}`,
    ];
    if (notes.trim()) {
      descriptionParts.push(`Notes: ${notes.trim()}`);
    }
    const description = descriptionParts.join('\n');

    try {
      if (business.isOnBookAPT === true) {
        await customServiceRequestService.createRequest({
          userId: userInfo.userId,
          userName: userInfo.name,
          userPhone: userInfo.phone,
          userEmail: userInfo.email,
          businessId: business.id,
          businessName: business.name,
          serviceName: service.trim(),
          description,
          preferredDate: date.trim(),
          preferredTime: time.trim(),
          additionalDetails: `Business: ${business.name} (${business.id})`,
          location: business.address,
          zipcode,
          pickupAddress: pickupAddress.trim(),
          dropoffAddress: dropoffAddress.trim(),
          requestSubtype: 'transportation_quote',
          status: 'pending',
        });
        Alert.alert(
          'Quote request sent',
          `Your ride details were sent to ${business.name}. They can reply with a quote or follow-up questions.`,
          [{ text: 'OK', onPress: () => onSubmit() }]
        );
        return;
      }

      const specialRequests = [
        'Please provide an estimate/quote for this transportation request before booking.',
        notes.trim() ? `Notes: ${notes.trim()}` : '',
      ]
        .filter(Boolean)
        .join(' ');

      const result = await appointmentRequestService.createRequest({
        userId: userInfo.userId,
        userName: userInfo.name,
        userPhone: userInfo.phone,
        userEmail: userInfo.email,
        businessName: business.name,
        businessPhone: business.phone || '',
        businessEmail: business.email,
        service: `Transportation quote: ${service.trim()}`,
        preferredDate: date.trim(),
        preferredTime: time.trim(),
        specialRequests,
        businessCategory: business.category,
        businessLocation: business.address,
        zipcode,
        pickupAddress: pickupAddress.trim(),
        dropoffAddress: dropoffAddress.trim(),
      });

      if (result.success) {
        Alert.alert(
          'Quote request sent',
          `We've sent your transportation quote request to ${business.name}. They'll receive your pickup and drop-off details.`,
          [{ text: 'OK', onPress: () => onSubmit() }]
        );
      } else if (result.phoneNotSmsCapable) {
        Alert.alert('Cannot send SMS', result.message || 'This number may not receive SMS.');
      } else if (result.reachedCap) {
        Alert.alert('Request limit', result.message || 'Request limit reached.');
      } else {
        Alert.alert('Error', result.message || 'Failed to send request.');
      }
    } catch (error) {
      console.error('Transportation quote request error:', error);
      Alert.alert('Error', 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ScrollView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={onCancel} style={styles.cancelButton}>
          <Text style={styles.cancelButtonText}>Cancel</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Request ride quote</Text>
        <View style={styles.placeholder} />
      </View>

      <View style={styles.content}>
        <View style={styles.businessInfo}>
          <Text style={styles.businessName}>{business.name}</Text>
          {!!business.address?.trim() && (
            <Text style={styles.businessAddress}>{business.address}</Text>
          )}
          {!!business.phone?.trim() && (
            <Text style={styles.businessPhone}>📞 {business.phone}</Text>
          )}
        </View>

        <View style={styles.formSection}>
          <Text style={styles.label}>Ride / service type *</Text>
          <View style={styles.serviceInputContainer}>
            <AppTextInput
              style={styles.input}
              placeholder="e.g. Airport shuttle, Taxi, Limousine"
              value={service}
              onChangeText={handleServiceChange}
              onFocus={() => serviceSuggestions.length > 0 && setShowSuggestions(true)}
              onBlur={handleBlur}
              autoCapitalize="words"
            />
            {showSuggestions && filteredSuggestions.length > 0 && (
              <View style={styles.suggestionsContainer}>
                <FlatList
                  data={filteredSuggestions.slice(0, 5)}
                  keyExtractor={(item, index) => `${item}-${index}`}
                  renderItem={({ item }) => (
                    <TouchableOpacity
                      style={styles.suggestionItem}
                      onPress={() => handleSuggestionSelect(item)}
                    >
                      <Text style={styles.suggestionText}>{item}</Text>
                    </TouchableOpacity>
                  )}
                  style={styles.suggestionsList}
                />
              </View>
            )}
          </View>

          <Text style={styles.label}>Pickup (from) *</Text>
          <AppTextInput
            style={[styles.input, styles.textAreaTall]}
            placeholder="Full address or landmark"
            value={pickupAddress}
            onChangeText={setPickupAddress}
            multiline
            textAlignVertical="top"
          />

          <Text style={styles.label}>Drop-off (to) *</Text>
          <AppTextInput
            style={[styles.input, styles.textAreaTall]}
            placeholder="Full address or landmark"
            value={dropoffAddress}
            onChangeText={setDropoffAddress}
            multiline
            textAlignVertical="top"
          />

          <Text style={styles.label}>Preferred date *</Text>
          <DatePicker value={date} onChange={setDate} minDate={minDate} placeholder="Select date" />

          <Text style={styles.label}>Preferred pickup time *</Text>
          <TimePicker value={time} onChange={setTime} placeholder="Select time" />

          <Text style={styles.label}>Extra details (optional)</Text>
          <AppTextInput
            style={[styles.input, styles.textArea]}
            placeholder="Passengers, luggage, flight number, accessibility needs…"
            value={notes}
            onChangeText={setNotes}
            multiline
            numberOfLines={4}
            textAlignVertical="top"
          />
        </View>

        <View style={styles.contactSection}>
          <Text style={styles.sectionTitle}>Your contact</Text>
          <Text style={styles.label}>Name *</Text>
          <AppTextInput
            style={styles.input}
            placeholder="Your name"
            value={userInfo.name}
            onChangeText={(text) => setUserInfo({ ...userInfo, name: text })}
          />
          <Text style={styles.label}>Phone *</Text>
          <AppTextInput
            style={styles.input}
            placeholder="(555) 123-4567"
            value={userInfo.phone}
            onChangeText={(text) => setUserInfo({ ...userInfo, phone: text })}
            keyboardType="phone-pad"
          />
          <Text style={styles.label}>Email</Text>
          <AppTextInput
            style={styles.input}
            placeholder="your@email.com"
            value={userInfo.email}
            onChangeText={(text) => setUserInfo({ ...userInfo, email: text })}
            keyboardType="email-address"
            autoCapitalize="none"
          />
        </View>

        <View style={styles.infoBox}>
          <Text style={styles.infoBoxText}>
            {business.isOnBookAPT === true
              ? `We'll record your request for ${business.name} with pickup and drop-off so they can send a quote.`
              : `We'll send your pickup and drop-off details to ${business.name} by SMS or email so they can reply with a quote.`}
          </Text>
        </View>

        <TouchableOpacity
          style={[styles.submitButton, loading && styles.submitButtonDisabled]}
          onPress={handleSubmit}
          disabled={loading}
        >
          <Text style={styles.submitButtonText}>{loading ? 'Sending…' : 'Request quote'}</Text>
        </TouchableOpacity>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  cancelButton: { padding: 8 },
  cancelButtonText: { fontSize: 16, color: '#007AFF' },
  headerTitle: { fontSize: 18, fontWeight: '600', color: '#333' },
  placeholder: { width: 60 },
  content: { padding: 16 },
  businessInfo: {
    backgroundColor: '#fff',
    padding: 16,
    borderRadius: 8,
    marginBottom: 16,
  },
  businessName: { fontSize: 20, fontWeight: 'bold', color: '#333', marginBottom: 8 },
  businessAddress: { fontSize: 14, color: '#666', marginBottom: 4 },
  businessPhone: { fontSize: 14, color: '#666' },
  formSection: {
    backgroundColor: '#fff',
    padding: 16,
    borderRadius: 8,
    marginBottom: 16,
  },
  sectionTitle: { fontSize: 18, fontWeight: '600', color: '#333', marginBottom: 16 },
  label: { fontSize: 14, fontWeight: '600', color: '#333', marginTop: 12, marginBottom: 8 },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    backgroundColor: '#fff',
  },
  textArea: { height: 100, paddingTop: 12 },
  textAreaTall: { minHeight: 72, paddingTop: 12 },
  serviceInputContainer: { position: 'relative', zIndex: 1 },
  suggestionsContainer: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ddd',
    marginTop: 4,
    maxHeight: 200,
    zIndex: 1000,
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  suggestionsList: { maxHeight: 200 },
  suggestionItem: { padding: 12, borderBottomWidth: 1, borderBottomColor: '#f0f0f0' },
  suggestionText: { fontSize: 14, color: '#333' },
  contactSection: {
    backgroundColor: '#fff',
    padding: 16,
    borderRadius: 8,
    marginBottom: 16,
  },
  infoBox: {
    backgroundColor: '#e7f3ff',
    padding: 12,
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: '#007AFF',
    marginBottom: 16,
  },
  infoBoxText: { fontSize: 14, color: '#333', lineHeight: 20 },
  submitButton: {
    backgroundColor: '#28a745',
    padding: 16,
    borderRadius: 8,
    alignItems: 'center',
    marginBottom: 24,
  },
  submitButtonDisabled: { backgroundColor: '#ccc' },
  submitButtonText: { color: '#fff', fontSize: 16, fontWeight: '600' },
});

export default TransportationQuoteRequestForm;
